"use client";

import TickIcon from "@/assets/TickIcon";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useMemo, useRef } from "react";

interface MetaItem {
  meta_id: number;
  meta_icon: string;
  meta_status: boolean;
  meta_alt_icon: string;
  meta_priority: number;
  meta_description: string;
  meta_description_text_color?: string;
}

interface ScholarData {
  title: string;
  /** Section background. Empty/undefined → no background applied. */
  background_color?: string;
  title_text_color?: string;
  sub_title?: string | null;
  sub_title_text_color?: string;
  desktop_bg_image: string;
  mobile_bg_image: string;
  alt_image?: string;
  metadata?: MetaItem[];
}

const Scholar1 = ({ data }: { data: any }) => {
  const isMobile = useIsMobile(800);
  const ref = useRef<HTMLElement>(null);

  const {
    background_color = "",
    title = "",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    desktop_bg_image = "",
    mobile_bg_image = "",
    alt_image = "",
    metadata = [],
  } = data?.data || {};

  // Sort metadata by priority and filter by status
  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: MetaItem) => item.meta_status)
      .sort((a: MetaItem, b: MetaItem) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // Track widget viewed
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "scholar_section");
  //   }
  // }, []);

  // Choose image based on device
  const image = isMobile
    ? mobile_bg_image || desktop_bg_image
    : desktop_bg_image || mobile_bg_image;

  if (!data?.data || sortedMetadata?.length === 0) {
    return null;
  }

  return (
    <div
      className="flex w-full justify-center"
      style={background_color ? { backgroundColor: background_color } : undefined}
    >
    <section
      ref={ref}
      className={cn(
        "flex items-center justify-center",
        "px-5 py-8 sm:px-8 sm:py-11",
        "flex flex-col sm:flex-row items-center justify-center w-full",
        "sm:w-[1008px] sm:gap-11",
        "md:w-[1158px] md:gap-[62px]",
        "lg:w-[1358px] lg:gap-[62px]",
        "xl:w-[1692px] xl:gap-[100px]",
      )}
    >
      {image && (
        <div
          className={cn(
            "shrink-0",
            "xs:w-[158px] xs:h-[180px]",
            "sm:w-[312px] sm:h-[362px]",
            "md:w-[316px] md:h-[364px]",
            "lg:w-[416px] lg:h-[480px]",
            "xl:w-[514px] xl:h-[590px]",
          )}
        >
          {image && (
            <Image
              src={image}
              alt={alt_image || "scholar image"}
              className="w-full h-full object-cover"
              width={600}
              height={600}
            />
          )}
        </div>
      )}

      {/* RIGHT CONTENT */}
      <div
        className={cn(
          "flex flex-col",
          "xs:gap-3",
          "sm:gap-5",
          "md:gap-[22px]",
          "lg:gap-6",
          "xl:gap-8",
        )}
      >
        {/* TITLE */}
        <h2
          className={cn(
            "xs:text-center xs:text-heading-6-semibold",
            "sm:text-left sm:text-heading-4-semibold",
            "md:text-heading-5-semibold",
            "lg:text-heading-6-semibold",
            "xl:text-heading-1-bold",
            "line-clamp-2",
            "type-h3",
          )}
          style={{ color: title_text_color }}
        >
          {title}
        </h2>

        {/* SUB TITLE */}
        {sub_title && (
          <div
            className={cn(
              "xs:text-center",
              "sm:text-left sm:text-body-3-regular",
              "md:text-body-2-regular",
              "lg:text-body-1-regular",
              "xl:text-new-body-24-regular",
              "line-clamp-2",
              "type-body-sm",
            )}
            style={{ color: sub_title_text_color }}
          >
            {sub_title}
          </div>
        )}

        <div
          className={cn(
            "flex flex-col",
            "xs:gap-6",
            "sm:gap-5",
            "md:gap-[22px]",
            "lg:gap-[26px]",
            "xl:gap-8",
          )}
        >
          {sortedMetadata?.map((meta, index) => (
            <div
              key={meta.meta_id || `scholar-meta-${index}`}
              className={cn(
                "flex items-center",
                "xs:gap-2",
                "sm:gap-2",
                "lg:gap-[10px]",
                "xl:gap-4",
              )}
            >
              <div
                className={cn(
                  "shrink-0",
                  "xs:size-[28px]",
                  "sm:size-[30px]",
                  "md:size-[34px]",
                  "lg:size-10",
                  "xl:size-12",
                )}
              >
                {meta?.meta_icon ? (
                  <Image
                    src={meta?.meta_icon || ""}
                    alt={meta?.meta_alt_icon || ""}
                    className={cn(
                      "object-contain",
                      "xs:size-[28px]",
                      "sm:size-[30px]",
                      "md:size-[30px]",
                      "lg:size-[30px]",
                      "xl:size-12",
                    )}
                    width={100}
                    height={100}
                  />
                ) : (
                  <TickIcon className="w-full h-full" />
                )}
              </div>
              <div
                className={cn(
                  "xs:text-body-2-regular line-clamp-4 sm:line-clamp-2",
                  "sm:text-body-3-regular",
                  "md:text-body-2-regular",
                  "lg:text-body-1-regular",
                  "xl:text-new-body-24-regular type-body-sm",
                )}
                style={{
                  color: meta.meta_description_text_color || "#000000",
                }}
              >
                {meta.meta_description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
    </div>
  );
};

export default Scholar1;
